import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Calendar, MapPin, CreditCard, MessageSquare, Search, Loader2, Star } from 'lucide-react';
import ReviewModal from '../components/ReviewModal';

const GuestDashboard = () => {
  const navigate = useNavigate();
  const { api, user, token } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();

  const [applications, setApplications] = useState([]);
  const [weddingsMap, setWeddingsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [payingApp, setPayingApp] = useState(null);
  const [selectedReviewWedding, setSelectedReviewWedding] = useState(null);
  const [selectedReviewedUser, setSelectedReviewedUser] = useState(null);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await api.get('/applications/guest/me');
      setApplications(response.data);
      
      // Fetch wedding details for each application
      for (const app of response.data) {
        const weddingResponse = await api.get(`/weddings/${app.wedding_id}`);
        setWeddingsMap((prev) => ({ ...prev, [app.wedding_id]: weddingResponse.data }));
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handlePayment = async (application) => {
    setPayingApp(application.id);
    try {
      const response = await api.post('/payments/checkout', {
        wedding_id: application.wedding_id,
        application_id: application.id,
        origin_url: window.location.origin,
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPayingApp(null);
    }
  };

  const pendingApps = applications.filter((app) => app.status === 'pending');
  const approvedApps = applications.filter((app) => app.status === 'approved');
  const paidApps = applications.filter((app) => app.payment_status === 'paid');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-secondary py-8">
          <div className="max-w-7xl mx-auto px-4">
            <Skeleton className="h-10 w-48 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="guest-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold">{t('dashboard.guest.title')}</h1>
              <p className="text-muted-foreground">Welcome back, {user?.name}</p>
            </div>
            <Button
              onClick={() => navigate('/weddings')}
              className="mt-4 md:mt-0 rounded-full"
              data-testid="browse-weddings-btn"
            >
              <Search className="mr-2 h-4 w-4" />
              {t('dashboard.guest.browseWeddings')}
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-3xl font-bold">{pendingApps.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Approved</p>
                    <p className="text-3xl font-bold">{approvedApps.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Confirmed</p>
                    <p className="text-3xl font-bold">{paidApps.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Applications */}
          <Tabs defaultValue="all">
            <TabsList className="mb-6">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({applications.length})
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending ({pendingApps.length})
              </TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">
                Approved ({approvedApps.length})
              </TabsTrigger>
              <TabsTrigger value="confirmed" data-testid="tab-confirmed">
                Confirmed ({paidApps.length})
              </TabsTrigger>
            </TabsList>

            {['all', 'pending', 'approved', 'confirmed'].map((tab) => {
              let filteredApps = applications;
              if (tab === 'pending') filteredApps = pendingApps;
              if (tab === 'approved') filteredApps = approvedApps;
              if (tab === 'confirmed') filteredApps = paidApps;

              return (
                <TabsContent key={tab} value={tab}>
                  {filteredApps.length === 0 ? (
                    <Card className="p-12 text-center">
                      <p className="text-muted-foreground mb-4">No applications yet</p>
                      <Button onClick={() => navigate('/weddings')} className="rounded-full">
                        <Search className="mr-2 h-4 w-4" />
                        Browse Weddings
                      </Button>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredApps.map((app) => {
                        const wedding = weddingsMap[app.wedding_id];
                        if (!wedding) return null;

                        return (
                          <Card key={app.id} className="overflow-hidden" data-testid={`app-card-${app.id}`}>
                            <div className="aspect-video bg-muted relative">
                              <img
                                src={wedding.images?.[0] || "https://images.pexels.com/photos/33485955/pexels-photo-33485955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
                                alt={wedding.title}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute top-2 right-2 flex gap-2">
                                <Badge
                                  variant={
                                    app.status === 'approved' ? 'default' :
                                    app.status === 'rejected' ? 'destructive' : 'secondary'
                                  }
                                >
                                  {t(`application.status.${app.status}`)}
                                </Badge>
                              </div>
                            </div>
                            <CardContent className="p-4">
                              <h3 className="font-semibold mb-2">{wedding.title}</h3>
                              <div className="space-y-2 text-sm text-muted-foreground mb-4">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-2 text-primary" />
                                  {formatDate(wedding.date)}
                                </div>
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-2 text-primary" />
                                  {wedding.location}
                                </div>
                              </div>

                              <div className="flex items-center justify-between pt-4 border-t">
                                <span className="font-semibold text-primary">
                                  {formatCurrency(wedding.price_per_guest, wedding.currency)}
                                </span>
                                
                                {app.status === 'approved' && app.payment_status !== 'paid' && (
                                  <Button
                                    size="sm"
                                    className="rounded-full"
                                    onClick={() => handlePayment(app)}
                                    disabled={payingApp === app.id}
                                    data-testid={`pay-btn-${app.id}`}
                                  >
                                    {payingApp === app.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Processing...
                                      </>
                                    ) : (
                                      <>
                                        <CreditCard className="mr-2 h-4 w-4" />
                                        {t('application.payment.pay')}
                                      </>
                                    )}
                                  </Button>
                                )}

                                {app.payment_status === 'paid' && (
                                  <div className="flex gap-2">
                                    <Badge className="bg-green-500">Paid</Badge>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => navigate(`/messages/${wedding.id}/${wedding.host_id}`)}
                                      data-testid={`msg-btn-${app.id}`}
                                    >
                                      <MessageSquare className="h-4 w-4" />
                                    </Button>
                                    {wedding.status === 'completed' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          setSelectedReviewWedding(wedding);
                                          setSelectedReviewedUser({
                                            id: wedding.host_id,
                                            name: wedding.host_name,
                                          });
                                        }}
                                        data-testid={`review-btn-${app.id}`}
                                      >
                                        <Star className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                )}

                                {app.status === 'pending' && (
                                  <Badge variant="outline">Awaiting Response</Badge>
                                )}

                                {app.status === 'rejected' && (
                                  <Badge variant="destructive">Rejected</Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>

      {selectedReviewWedding && selectedReviewedUser && (
        <ReviewModal
          isOpen={!!selectedReviewWedding}
          onClose={() => {
            setSelectedReviewWedding(null);
            setSelectedReviewedUser(null);
          }}
          wedding={selectedReviewWedding}
          reviewedUser={selectedReviewedUser}
          token={token}
          onSuccess={() => {
            setSelectedReviewWedding(null);
            setSelectedReviewedUser(null);
          }}
        />
      )}
    </Layout>
  );
};

export default GuestDashboard;
